import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { useMutation } from "react-query";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaCheckCircle,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import { ACCENT, ACCENT_TINT, TEAL, INK, MUTED } from "../products/Products";
import {isKenyanMobile, newSubmissionId, readSource, submitLead, useLeadForm} from "../../hooks/lead";


const CONTACT_LABELS = {
  call: "Phone call",
  sms: "SMS",
  whatsapp: "WhatsApp",
  email: "Email",
};
const TIME_LABELS = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  anytime: "Any time",
};

const isEmpty = (v) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length);
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/*
 * One question per screen. Steps are built from fixed contact fields plus
 * whatever questions marketing has set up in the back office.
 *   kind "text"    -> fields[field] or answers[key]
 *   kind "single"  -> one choice      kind "multi" -> several choices
 *   kind "consent" -> last screen, holds the Send button
 * Each step's `errorKey` matches the API's error keys, so server errors land
 * on the right screen.
 */
const buildSteps = (form, fields) => {
  // Fixed contact details first, then marketing's questions, then contact
  // preferences and consent.
  const steps = [
    {
      errorKey: "name",
      kind: "text",
      field: "name",
      required: true,
      title: "What's your name?",
      autoComplete: "name",
    },
    {
      errorKey: "phone",
      kind: "text",
      field: "phone",
      required: true,
      title: "What's your phone number?",
      hint: "We'll use it to get back to you.",
      type: "tel",
      inputMode: "tel",
      autoComplete: "tel",
      placeholder: "07XX XXX XXX",
    },
    {
      errorKey: "business_name",
      kind: "text",
      field: "business_name",
      title: "What's your business called?",
      hint: "Skip this if you're not buying or selling as a business.",
      autoComplete: "organization",
    },
    {
      errorKey: "county",
      kind: "select",
      field: "county",
      required: true,
      title: "Which county are you in?",
      placeholder: "Choose your county",
      options: form?.counties || [],
    },
    {
      errorKey: "town",
      kind: "text",
      field: "town",
      title: "Which town?",
      placeholder: "For example Westlands",
      autoComplete: "address-level2",
    },
  ];
  (form?.questions || []).forEach((q) => {
    const base = {
      errorKey: `answers.${q.key}`,
      answerKey: q.key,
      required: q.required,
      title: q.label,
      hint: q.help_text,
    };
    if (q.answer_type === "text")
      steps.push({ ...base, kind: "text", maxLength: 1000 });
    else
      steps.push({
        ...base,
        kind: q.answer_type === "multi_select" ? "multi" : "single",
        options: q.options || [],
      });
  });
  steps.push(
    {
      errorKey: "preferred_contact_method",
      kind: "single",
      field: "preferred_contact_method",
      title: "How should we contact you?",
      options: (form?.preferred_contact_methods || []).map((v) => ({
        id: v,
        name: CONTACT_LABELS[v] || v,
      })),
    },
    {
      errorKey: "best_time_to_contact",
      kind: "single",
      field: "best_time_to_contact",
      title: "When is the best time to reach you?",
      options: (form?.best_times_to_contact || []).map((v) => ({
        id: v,
        name: TIME_LABELS[v] || v,
      })),
    },
    {
      errorKey: "email",
      kind: "text",
      field: "email",
      // Required only if they chose email as the way to reach them.
      required: fields.preferred_contact_method === "email",
      title: "What's your email address?",
      hint:
        fields.preferred_contact_method === "email"
          ? "You asked us to reach you by email."
          : undefined,
      type: "email",
      inputMode: "email",
      autoComplete: "email",
    },
    { errorKey: "consent", kind: "consent", title: "Almost done" },
  );
  return steps;
};

const Connect = () => {
  const history = useHistory();
  const location = useLocation();
  const source = useMemo(() => readSource(location.search), [location.search]);
  const { data: form, isLoading, isError, refetch } = useLeadForm();

  const startedAt = useRef(Date.now());
  const submissionId = useRef(newSubmissionId());
  const inputRef = useRef(null);

  const [fields, setFields] = useState({
    name: "",
    phone: "",
    email: "",
    business_name: "",
    county: "",
    town: "",
    preferred_contact_method: "",
    best_time_to_contact: "",
    consent: false,
    website: "", // honeypot: real people never see or fill this
  });
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [done, setDone] = useState(null);

  const steps = useMemo(() => buildSteps(form, fields), [form, fields]);

  // The current screen lives in the URL hash (#1, #2, ...) so the phone's back
  // button steps back through questions instead of leaving the form.
  const hashStep = parseInt((location.hash || "").replace("#", ""), 10);
  const index = Number.isFinite(hashStep)
    ? Math.min(Math.max(hashStep - 1, 0), steps.length - 1)
    : 0;
  const step = steps[index];
  const isLast = index === steps.length - 1;

  // Arriving mid-form (refresh, shared link with #5) starts again at question 1.
  useEffect(() => {
    if (location.hash) history.replace({ ...location, hash: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move focus to the new question so keyboard and screen-reader users follow along.
  useEffect(() => {
    if (!form || done) return;
    const t = setTimeout(
      () => inputRef.current && inputRef.current.focus({ preventScroll: true }),
      30,
    );
    window.scrollTo({ top: 0 });
    return () => clearTimeout(t);
  }, [index, form, done]);

  const valueOf = (s) =>
    s.kind === "consent"
      ? fields.consent
      : s.field
        ? fields[s.field]
        : answers[s.answerKey];

  const setValue = (s, value) => {
    if (s.field) setFields((f) => ({ ...f, [s.field]: value }));
    else setAnswers((a) => ({ ...a, [s.answerKey]: value }));
    setErrors((e) => ({ ...e, [s.errorKey]: undefined }));
  };

  const validate = (s) => {
    const v = valueOf(s);
    if (s.kind === "consent") return null;
    if (s.required && isEmpty(typeof v === "string" ? v.trim() : v)) {
      if (s.kind === "multi") return "Choose at least one.";
      if (s.kind === "single") return "Choose one.";
      if (s.kind === "select") return "Choose your county from the list.";
      if (s.field === "name") return "Enter your name.";
      if (s.field === "phone") return "Enter your phone number.";
      if (s.field === "email")
        return "Enter your email so we can reach you there.";
      return "Please answer this question.";
    }
    if (s.field === "name" && v.trim().length < 2) return "Enter your name.";
    if (s.field === "phone" && !isKenyanMobile(v))
      return "Enter a Kenyan mobile number, for example 0712 345 678.";
    if (s.field === "email" && v.trim() && !EMAIL_RE.test(v.trim()))
      return "Enter a valid email address, or skip this.";
    return null;
  };

  const goTo = (i) =>
    history.push({ ...location, hash: i === 0 ? "" : `#${i + 1}` });

  const { mutate, isLoading: sending } = useMutation(submitLead, {
    onSuccess: (res) => setDone({ reference: res.data?.reference }),
    onError: (err) => {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      if (status === 400 && data.errors) {
        setErrors(data.errors);
        const first = steps.findIndex((s) => data.errors[s.errorKey]);
        if (first >= 0) goTo(first);
        else setBanner(data.message || "Some answers need another look.");
      } else if (status === 429) {
        setBanner(
          "Too many attempts from this connection. Wait a few minutes, then send again.",
        );
      } else if (!err?.response) {
        setBanner(
          "Your details weren't sent. Check your connection and send again; nothing is lost.",
        );
      } else {
        setBanner(
          data.message || "Your details weren't sent. Please send again.",
        );
      }
    },
  });

  const send = () => {
    // Re-check every screen in case something changed since it was answered.
    const firstBad = steps.findIndex((s) => validate(s));
    if (firstBad >= 0) {
      setErrors((e) => ({
        ...e,
        [steps[firstBad].errorKey]: validate(steps[firstBad]),
      }));
      goTo(firstBad);
      return;
    }
    const trimmed = (v) => (v && v.trim()) || undefined;
    mutate({
      submission_id: submissionId.current,
      name: fields.name.trim(),
      phone: fields.phone.trim(),
      email: trimmed(fields.email),
      business_name: trimmed(fields.business_name),
      county: fields.county || undefined,
      town: trimmed(fields.town),
      preferred_contact_method: fields.preferred_contact_method || undefined,
      best_time_to_contact: fields.best_time_to_contact || undefined,
      consent: fields.consent,
      platform: source.platform || undefined,
      campaign: source.campaign || undefined,
      answers,
      fill_time_ms: Date.now() - startedAt.current,
      website: fields.website,
    });
  };

  const onNext = (e) => {
    e.preventDefault();
    setBanner("");
    const problem = validate(step);
    if (problem) {
      setErrors((x) => ({ ...x, [step.errorKey]: problem }));
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    if (isLast) send();
    else goTo(index + 1);
  };

  const seo = (
    <Seo
      title="Markiti & Keep It Cool | Business registration"
      description="Buy, sell or get cold-chain solutions with Markiti and Keep It Cool. Register your interest and our team will get back to you."
      path="/connect"
    />
  );

  const header = (
    <Header>
      <FormTitle>
        Markiti &amp; Keep It Cool — Business Registration Form
      </FormTitle>
    </Header>
  );

  if (done) {
    const firstName = fields.name.trim().split(/\s+/)[0];
    const method = CONTACT_LABELS[fields.preferred_contact_method];
    const time = TIME_LABELS[fields.best_time_to_contact];
    return (
      <Page>
        {seo}
        {header}
        <Card>
          <CardBody $center>
            <FaCheckCircle size={52} color={TEAL} aria-hidden="true" />
            <QuestionTitle as="h2">Thank you, {firstName}</QuestionTitle>
            <DoneText>
              We've received your details. Someone from our team will contact
              you
              {method ? ` by ${method}` : ""}
              {time && fields.best_time_to_contact !== "anytime"
                ? ` in the ${time.toLowerCase()}`
                : ""}{" "}
              on {fields.phone.trim()}.
            </DoneText>
            {done.reference && (
              <DoneText>
                Your reference: <strong>{done.reference}</strong>
              </DoneText>
            )}
          </CardBody>
          <BarRow $single>
            <BarButton
              type="button"
              $alignRight
              onClick={() => history.push("/")}
            >
              Visit markiti.co.ke <FaArrowRight aria-hidden="true" />
            </BarButton>
          </BarRow>
        </Card>
      </Page>
    );
  }

  const value = form ? valueOf(step) : undefined;
  const error = form ? errors[step.errorKey] : null;
  const nextLabel = isLast ? (sending ? "Sending…" : "Submit") : "Next";

  return (
    <Page>
      {seo}
      {header}

      {isLoading && (
        <Card aria-busy="true">
          <CardBody $center>
            <Muted>Loading the form…</Muted>
          </CardBody>
        </Card>
      )}

      {isError && (
        <Card>
          <CardBody $center>
            <DoneText>
              The form didn't load. Check your connection and try again.
            </DoneText>
          </CardBody>
          <BarRow $single>
            <BarButton type="button" $alignRight onClick={() => refetch()}>
              Try again
            </BarButton>
          </BarRow>
        </Card>
      )}

      {form && (
        <Card as="form" onSubmit={onNext} noValidate>
          <CardBody key={index}>
            <StepView
              step={step}
              value={value}
              error={error}
              inputRef={inputRef}
              onChange={(v) => setValue(step, v)}
              consent={fields.consent}
              onConsent={(v) => setFields((f) => ({ ...f, consent: v }))}
            />
            {banner && <Banner role="alert">{banner}</Banner>}
          </CardBody>

          <Honeypot aria-hidden="true">
            <label>
              Website
              <input
                tabIndex={-1}
                autoComplete="off"
                value={fields.website}
                onChange={(e) =>
                  setFields((f) => ({ ...f, website: e.target.value }))
                }
              />
            </label>
          </Honeypot>

          <BarRow>
            {index > 0 ? (
              <BarButton type="button" onClick={() => history.goBack()}>
                <FaArrowLeft aria-hidden="true" /> Back
              </BarButton>
            ) : (
              <span />
            )}
            <BarButton type="submit" $alignRight disabled={sending}>
              {nextLabel} <FaArrowRight aria-hidden="true" />
            </BarButton>
          </BarRow>
        </Card>
      )}

      {form && (
        <Footnote aria-live="polite">
          Question {index + 1} of {steps.length}
          {isLast && (
            <>
              <br />
              We use these details only to respond to you. See our{" "}
              <a href="/privacy-policy">privacy policy</a>.
            </>
          )}
        </Footnote>
      )}
    </Page>
  );
};

export default Connect;

/* ------------------------------------------------------------- one screen */

const StepView = ({
  step,
  value,
  error,
  inputRef,
  onChange,
  consent,
  onConsent,
}) => {
  const titleId = "lead-step-title";
  const errorId = "lead-step-error";
  const hintId = "lead-step-hint";
  const describedBy =
    [step.hint && hintId, error && errorId].filter(Boolean).join(" ") ||
    undefined;

  const title = (
    <QuestionTitle
      as={step.kind === "text" || step.kind === "select" ? "label" : "h2"}
      id={titleId}
      htmlFor={
        step.kind === "text" || step.kind === "select"
          ? "lead-step-input"
          : undefined
      }
    >
      {step.title}
      {step.required && <Required aria-hidden="true">*</Required>}
    </QuestionTitle>
  );
  const hint = step.hint && <Hint id={hintId}>{step.hint}</Hint>;
  const errorMsg = error && (
    <ErrorMsg id={errorId} role="alert">
      {error}
    </ErrorMsg>
  );

  if (step.kind === "consent") {
    return (
      <>
        <QuestionTitle as="h2" id={titleId}>
          {step.title}
        </QuestionTitle>
        <OptionList>
          <Option $checked={consent}>
            <VisuallyHidden
              as="input"
              ref={inputRef}
              type="checkbox"
              checked={consent}
              onChange={(e) => onConsent(e.target.checked)}
            />
            <Box $checked={consent} aria-hidden="true">
              {consent && <FaCheck size={11} />}
            </Box>
            Send me offers and updates from Markiti by SMS or WhatsApp.
          </Option>
        </OptionList>
      </>
    );
  }

  if (step.kind === "select") {
    return (
      <>
        {title}
        {hint}
        <StyledSelect
          ref={inputRef}
          id="lead-step-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          aria-required={!!step.required}
          aria-describedby={describedBy}
          $invalid={!!error}
          $empty={!value}
        >
          <option value="">{step.placeholder}</option>
          {step.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </StyledSelect>
        {errorMsg}
      </>
    );
  }

  if (step.kind === "text") {
    return (
      <>
        {title}
        {hint}
        <StyledInput
          ref={inputRef}
          id="lead-step-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          type={step.type || "text"}
          inputMode={step.inputMode}
          autoComplete={step.autoComplete}
          placeholder={step.placeholder}
          maxLength={step.maxLength}
          aria-invalid={!!error}
          aria-required={!!step.required}
          aria-describedby={describedBy}
          $invalid={!!error}
        />
        {errorMsg}
      </>
    );
  }

  const multi = step.kind === "multi";
  const isChecked = (id) => (multi ? (value || []).includes(id) : value === id);
  const choose = (id) => {
    if (multi) {
      const current = value || [];
      onChange(
        current.includes(id)
          ? current.filter((x) => x !== id)
          : [...current, id],
      );
    } else {
      // Tapping the selected option of an optional question clears it.
      onChange(value === id && !step.required ? "" : id);
    }
  };

  return (
    <div
      role={multi ? "group" : "radiogroup"}
      aria-labelledby={titleId}
      aria-describedby={describedBy}
    >
      {title}
      {hint}
      <OptionList>
        {step.options.map((o, i) => {
          const checked = isChecked(o.id);
          return (
            <Option key={o.id} $checked={checked} $invalid={!!error}>
              <VisuallyHidden
                as="input"
                ref={i === 0 ? inputRef : undefined}
                type={multi ? "checkbox" : "radio"}
                name="lead-step"
                checked={checked}
                onChange={() => choose(o.id)}
                onClick={!multi && checked ? () => choose(o.id) : undefined}
              />
              <Box $checked={checked} $round={!multi} aria-hidden="true">
                {checked && (multi ? <FaCheck size={11} /> : <Dot />)}
              </Box>
              {o.name}
            </Option>
          );
        })}
      </OptionList>
      {errorMsg}
    </div>
  );
};

/* -------------------------------------------------------------- styles */

// Brand colours from the codebase: purple ACCENT (#494395), turquoise TEAL (#2AB4AA).
const BORDER = "#c9c6d6";
const ERROR = "#d92d20";

const quietMotion = css`
  transition:
    border-color 140ms ease,
    background-color 140ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${TEAL};
  padding: 0 1rem 3rem;
`;
const Header = styled.header`
  padding: 3rem 0 2.5rem;
  text-align: center;
  @media (max-width: 600px) {
    padding: 2rem 0 1.5rem;
  }
`;
const FormTitle = styled.h1`
  max-width: 720px;
  margin: 0 auto;
  color: #fff;
  font-weight: 500;
  font-size: clamp(1.3rem, 3.2vw, 2.25rem);
  line-height: 1.45;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;
const Card = styled.div`
  max-width: 832px;
  margin: 0 auto;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(20, 60, 58, 0.18);
`;
const CardBody = styled.div`
  padding: 2.5rem 3.1rem 2.75rem;
  ${(p) =>
    p.$center &&
    css`
      text-align: center;
    `}
  @media (max-width: 600px) {
    padding: 1.75rem 1.2rem 2rem;
  }
`;
const QuestionTitle = styled.h2`
  display: block;
  margin: 0 0 1.4rem;
  text-align: center;
  font-size: 1.6rem;
  line-height: 1.35;
  font-weight: 500;
  color: ${INK};
  ${CardBody} svg + & {
    margin-top: 1rem;
  }
  @media (max-width: 600px) {
    font-size: 1.3rem;
    margin-bottom: 1.1rem;
  }
`;
const Required = styled.span`
  color: ${ERROR};
  margin-left: 0.15rem;
`;
const Hint = styled.p`
  margin: -0.8rem 0 1.2rem;
  text-align: center;
  font-size: 0.9rem;
  color: ${MUTED};
  line-height: 1.45;
`;
const StyledSelect = styled.select`
  width: 100%;
  padding: 0.85rem 0.95rem;
  border-radius: 4px;
  border: 1.5px solid ${(p) => (p.$invalid ? ERROR : BORDER)};
  font-size: 1.05rem; /* 16px+ stops iOS zooming into the field */
  color: ${(p) => (p.$empty ? MUTED : INK)};
  background: #fff;
  ${quietMotion}
  &:focus {
    outline: none;
    border-color: ${(p) => (p.$invalid ? ERROR : ACCENT)};
    box-shadow: 0 0 0 3px ${(p) => (p.$invalid ? "#fee4e2" : ACCENT_TINT)};
  }
`;
const StyledInput = styled.input`
  width: 100%;
  padding: 0.85rem 0.95rem;
  border-radius: 4px;
  border: 1.5px solid ${(p) => (p.$invalid ? ERROR : BORDER)};
  font-size: 1.05rem; /* 16px+ stops iOS zooming into the field */
  color: ${INK};
  background: #fff;
  ${quietMotion}
  &:focus {
    outline: none;
    border-color: ${(p) => (p.$invalid ? ERROR : ACCENT)};
    box-shadow: 0 0 0 3px ${(p) => (p.$invalid ? "#fee4e2" : ACCENT_TINT)};
  }
`;
const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;
const Option = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-height: 3.1rem;
  padding: 0.7rem 1rem;
  border-radius: 4px;
  border: 1.5px solid
    ${(p) => (p.$checked ? ACCENT : p.$invalid ? ERROR : BORDER)};
  background: ${(p) => (p.$checked ? ACCENT_TINT : "#fff")};
  color: ${INK};
  font-size: 1.02rem;
  line-height: 1.35;
  cursor: pointer;
  user-select: none;
  ${quietMotion}
  &:hover {
    border-color: ${ACCENT};
  }
  &:has(input:focus-visible) {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }
  @supports not selector(:has(a)) {
    &:focus-within {
      outline: 2px solid ${ACCENT};
      outline-offset: 2px;
    }
  }
`;
const Box = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: ${(p) => (p.$round ? "50%" : "4px")};
  border: 2px solid ${(p) => (p.$checked ? ACCENT : "#a9a5bd")};
  background: ${(p) => (p.$checked && !p.$round ? ACCENT : "#fff")};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  ${quietMotion}
`;
const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${ACCENT};
`;
const ErrorMsg = styled.div`
  color: ${ERROR};
  font-size: 0.9rem;
  margin-top: 0.75rem;
`;
const BarRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$single ? "1fr" : "1fr 1fr")};
  background: ${ACCENT};
  & > * + * {
    border-left: 1px solid rgba(255, 255, 255, 0.22);
  }
`;
const BarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$alignRight ? "flex-end" : "flex-start")};
  gap: 0.6rem;
  min-height: 3.6rem;
  padding: 0 2rem;
  background: transparent;
  border: 0;
  color: #fff;
  font-size: 1.05rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
  &:disabled {
    opacity: 0.65;
    cursor: progress;
  }
  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -5px;
  }
  @media (max-width: 600px) {
    padding: 0 1.2rem;
    font-size: 0.98rem;
  }
`;
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
const Honeypot = styled.div`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;
const Banner = styled.div`
  margin-top: 1.2rem;
  background: #fef3f2;
  border: 1px solid #fecdca;
  color: #b42318;
  border-radius: 4px;
  padding: 0.8rem 0.95rem;
  font-size: 0.9rem;
  line-height: 1.45;
`;
const Footnote = styled.p`
  max-width: 832px;
  margin: 1.1rem auto 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.85rem;
  line-height: 1.6;
  a {
    color: #fff;
  }
`;
const DoneText = styled.p`
  margin: 0 auto 0.6rem;
  max-width: 46ch;
  color: ${INK};
  font-size: 1.02rem;
  line-height: 1.55;
`;
const Muted = styled.p`
  margin: 0;
  color: ${MUTED};
`;
